import type {JSONSchema7} from 'json-schema';

export type ProcessorDefinition = {
  label: string;
  name: ProcessorType;
  description: string;
  inputs: StageDefinitionMap;
  outputs: StageDefinitionMap;
  schema: JSONSchema7;
  category: [string, string];
  examples?: ProcessorExample[];
  property_categories?: ProcessorPropertyCategories;
};

export type StageDataSource = 'real_time' | 'time_series' | 'telemetry_service_warnings';

export type ProcessorType = string;
export type ProcessorPropertyCategories = Record<string, ProcessorPropertyCategory>;
export type ProcessorPropertyCategory = 'graph' | 'telemetry' | 'processing';
export const PROCESSOR_PROPERTY_CATEGORIES: ProcessorPropertyCategory[] = ['graph', 'telemetry', 'processing'];

export type StageDefinition = {
  types: StageType[];
  required: boolean;
  description?: string;
  allow_column_wildcard?: boolean;
};
export type StageDefinitionMap = Record<string, StageDefinition>;

export type StageType = {
  dynamic: boolean;
  type: 'table';
  keys: Record<string, StageKeyType>;
  values: Record<string, StageValueSchema[]>;
  meta?: Record<string, any>;
};

export type StageKeyType = 'string' | 'float' | 'integer' | 'bool' | 'any';
export type StageValueType = 'string' | 'bool' |
  'u8' | 'u16' | 'u32' | 'u64' | // unsigned integer
  's8' | 's16' | 's32' | 's64' | // signed integer
  'f32' | 'f64'; // float

export type StageValueSchema = {
  name: string;
  type: StageValueType;
  title?: string;
  description?: string;
  properties?: JSONSchema7;
  possible_values?: string[];
}

export type Probe = {
  anomaly_count: number;
  description: string;
  disabled: boolean;
  host_node: string;
  iba_unit: string;
  id: string;
  label: string;
  last_error: any | null;
  predefined_probe: string;
  probe_state: any;
  processors: ProbeProcessor[];
  referencing_dashboards: Array<{
    id: string;
    label: string;
    predefined_dashboard: string;
  }>;
  stages: ProbeStage[];
  state: 'error' | 'operational' | 'configuring' | 'maintenance';
  task_error: string;
  task_state: 'running';
}

export type ProbeProcessor = {
  name: string;
  inputs: ProcessorInputMap;
  outputs: Record<string, string>,
  properties: Record<string, any>;
  task_error?: any;
  type: ProcessorType;
}

export type ProbeStage = {
  anomaly_count: number;
  description: string;
  dynamic: boolean;
  enable_metric_logging: boolean;
  graph_annotation_properties: any;
  name: string;
  retention_duration: number;
  units: Record<string, string>;
  keys: Record<string, StageKeyType>,
  values: Record<string, StageValueSchema>,
  warnings: number;
}

export type ProcessorInput = {
  column?: string;
  stage?: string;
};

export type ProcessorInputMap = Record<string, ProcessorInput>;

export type ProcessorCategory = {
  description: string;
  label: string;
  name: string;
  subcategories: Record<string, ProcessorSubcategory>;
};

export type ProcessorCategories = Record<string, ProcessorCategory>;

export type ProcessorSubcategory = {
  description: string;
  label: string;
  name: string;
};

export type ProcessorExample = {
  name: string;
  inputs: Record<string, ProcessorExampleStage[]>;
  outputs: Record<string, ProcessorExampleStage[]>;
  properties: Record<string, any>;
}

export type ProcessorExampleStage = {
  keys: any;
  values: any;
}

export type RenderingStrategy = {
  ValueRenderer?: any,
  SearchComponent?: any,
  StageDataRenderer?: any,
  aggregationType?: string,
  alwaysUseContext?: boolean,
  clearFilterOnContextTrigger?: boolean,
  defaultFilter?: string,
  extraColumns?: Array<any>,
  filterDeserializer?: any,
  filterSerializer?: any,
  filterTransformer?: (filter: any) => any,
  getRenderer?: any,
  graphCombiningAvailable?: any,
  hiddenContextByDefault?: boolean,
  includePrecedingProcessorAnomalies?: boolean,
  noAnomalyHighlighting?: boolean,
  noCustomization?: boolean,
  noDataMessage?: string,
  noPagination?: boolean,
  noSpotlightMode?: boolean,
  shouldFetchData?: (args: {filters: any}) => boolean,
  showTimestamp?: boolean,
  sortableByValue?: boolean,
  usePatternDescriptionAsValueDescription?: boolean,
};

export type WidgetComponentType = 'stage';

export type Widget =
  {type: 'stage'} & StageWidget;

type BaseWidget = {
  label: string;
  description: string;
}

export type StageWidget = BaseWidget & {
  aggregation_period: number;
  aggregation_type: string;
  anomalous_only: boolean;
  combine_graphs: 'none' | 'linear' | 'stacked';
  data_source: StageDataSource;
  filter: any;
  max_items: number;
  orderby: string;
  probe_id: string;
  show_context: boolean;
  spotlight_mode: boolean;
  stage_name: string;
  time_series_duration: number;
  visible_columns: string[];
};

export type DashboardBase = {
  description: string;
  grid: Widget[][];
}

export type PredefinedDashboard = DashboardBase & {
  autoscaling: boolean;
  condition: {
    description: string;
    type: 'graph_query' | 'never' | string;
    properties?: {
      remove?: boolean;
    }
  };
  disabled: boolean;
  dynamic: boolean;
  name: string;
  probes: object;
  schema: JSONSchema7;
}

export type Dashboard = DashboardBase & {
  created_at: string;
  created_by: string | null;
  default: boolean;
  id: string;
  label: string;
  predefined_dashboard: string;
  predefined_parameters: object;
  updated_at: string;
  updated_by: string | null;
}

export type WidgetFetchDataParams = {
  blueprintId: string;
  isFreeform: boolean;
  probes?: Probe[];
  processorDefinitions: ProcessorDefinition[];
  routes: any;
  signal?: AbortSignal;
  widget: Widget;
  widgetType: string;
};
